import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { hashHistory } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExchangeAlt,
  faCloudUploadAlt,
  faFileVideo,
  faSignInAlt,
  faEdit,
  faSave,
  faCamera,
  faPlusSquare
} from "@fortawesome/free-solid-svg-icons";
import { actions as assetActions } from "../../actions/asset";
import { actions as appActions } from "../../actions/app";
import AppMode from "../../constants/appMode";
import download from "../../util/download";
import { makeGetConfigValue } from "../../selectors/config";
import { generateShortcutText } from "../../util/keyShortcutUtils";
import localize from "../../util/localizeUtils";
import AssetTypes from "../../constants/assetTypes";
import Toolbar from "../Toolbar/Toolbar";
import ToolbarItem from "../Toolbar/ToolbarItem";

const Mousetrap = window.Mousetrap;

class EditorSideBarContent extends React.Component {
  constructor(props) {
    super(props);
    if (!props.connectionsLoaded) {
      props.checkConnectionStatus();
    }
  }
  componentDidMount() {
    this.bindShortcuts();
  }

  componentWillUnmount() {
    this.unbindShortcuts();
  }

  bindShortcuts = () => {
    const { insert, publish, modify } = this.props.shortcuts;
    Mousetrap.bindGlobal(insert, () => {
      if (this.props.isMosObject && !this.props.generatingPreview) {
        this.props.createMosObjectFromExisting({
          sendFromCamio: this.props.sendFromCamio,
          includeRunningOrderInfo: this.props.includeRunningOrderInfo
        });
      } else if (!this.props.generatingPreview) {
        this.props.createNewMosObject({
          sendFromCamio: this.props.sendFromCamio,
          includeRunningOrderInfo: this.props.includeRunningOrderInfo
        });
      }
    });
    Mousetrap.bindGlobal(publish, () => {
      this.props.showPublishingDialog();
    });
    Mousetrap.bindGlobal(modify, () => {
      if (
        this.props.editing &&
        !this.props.forceEditNew &&
        !this.props.generatingPreview
      ) {
        this.props.updateMosObject({
          sendFromCamio: this.props.sendFromCamio,
          includeRunningOrderInfo: this.props.includeRunningOrderInfo
        });
      }
    });
  };

  unbindShortcuts = () => {
    const { insert, publish, modify, saveImage } = this.props.shortcuts;
    Mousetrap.unbind(insert);
    Mousetrap.unbind(publish);
    Mousetrap.unbind(modify);
    Mousetrap.unbind(saveImage);
  };

  downloadImage = () => {
    if (this.props.previewImg) {
      download(
        `data:image/jpg;base64,${this.props.previewImg}`,
        `${this.props.title ? this.props.title : "Preview"}.png`,
        "image/png"
      );
    } else {
      download(this.props.thumbnailUrl);
    }
  };

  handleInsertClick = e => {
    const crtlKeyHeld = e.ctrlKey;
    if (this.props.isMosObject) {
      this.props
        .createMosObjectFromExisting({
          sendFromCamio: this.props.sendFromCamio,
          includeRunningOrderInfo: this.props.includeRunningOrderInfo
        })
        .then(() => this.props.setAppMode(AppMode.NORMAL))
        .then(() => {
          if (this.props.onComplete) {
            return this.props.onComplete();
          }
          if (crtlKeyHeld) {
            return Promise.resolve();
          }
          return hashHistory.push("/assets");
        })
        .catch(error => {
          console.log(error);
        });
    } else {
      this.props
        .createNewMosObject({
          sendFromCamio: this.props.sendFromCamio,
          includeRunningOrderInfo: this.props.includeRunningOrderInfo
        })
        .then(() => this.props.setAppMode(AppMode.NORMAL))
        .then(() => {
          if (this.props.onComplete) {
            return this.props.onComplete();
          }
          if (crtlKeyHeld) {
            return Promise.resolve();
          }
          return hashHistory.push("/assets");
        })
        .catch(error => {
          console.log(error);
        });
    }
  };

  handleReplaceWithNewClick = e => {
    const crtlKeyHeld = e.ctrlKey;
    this.props
      .replaceMosObjectWithNew({
        sendFromCamio: this.props.sendFromCamio,
        includeRunningOrderInfo: this.props.includeRunningOrderInfo
      })
      .then(() => this.props.setAppMode(AppMode.NORMAL))
      .then(() => {
        if (this.props.onComplete) {
          return this.props.onComplete();
        }
        if (crtlKeyHeld) {
          return Promise.resolve();
        }
        return hashHistory.push("/assets");
      })
      .catch(error => {
        console.log(error);
      });
  };

  handleUpdateMosObjectClick = e => {
    const crtlKeyHeld = e.ctrlKey;
    this.props
      .updateMosObject({
        sendFromCamio: this.props.sendFromCamio,
        includeRunningOrderInfo: this.props.includeRunningOrderInfo
      })
      .then(() => this.props.setAppMode(AppMode.NORMAL))
      .then(() => {
        if (this.props.onComplete) {
          return this.props.onComplete();
        }
        if (crtlKeyHeld) {
          return Promise.resolve();
        }
        return hashHistory.push("/assets");
      })
      .catch(error => {
        console.log(error);
      });
  };

  handlePreviewClick = () => {
    this.props.generatePreview();
    this.requestMosXml();
  };

  requestMosXml = () => {
    if (!this.props.inserting) {
      if (this.props.isMosObject) {
        if (this.props.editing) {
          if (this.props.forceEditNew || !this.props.isModifierActive) {
            this.props.replaceMosObjectWithNew({
              suppressContainerMessage: true
            });
          } else {
            this.props.updateMosObject({ suppressContainerMessage: true });
          }
        } else {
          this.props.createMosObjectFromExisting({
            suppressContainerMessage: true
          });
        }
      } else if (this.props.swapping) {
        this.props.replaceMosObjectWithNew({ suppressContainerMessage: true });
      } else {
        this.props.createNewMosObject({ suppressContainerMessage: true });
      }
    }
  };

  renderIcon = faIcon => <FontAwesomeIcon fixedWidth size="2x" icon={faIcon} />;

  renderMosControls = () => {
    const {
      editing,
      forceEditNew,
      isModifierActive,
      placeholderId,
      swapping,
      includeRunningOrderInfo,
      generatingPreview
    } = this.props;
    if (placeholderId || (editing && forceEditNew)) {
      return (
        <ToolbarItem
          key="replaceButton"
          text={generateShortcutText(
            localize("replaceButton"),
            isModifierActive
          )}
          icon={this.renderIcon(faEdit)}
          onClick={this.handleReplaceWithNewClick}
        />
      );
    }
    if (editing) {
      const buttons = [
        <ToolbarItem
          key="replaceWithNewButton"
          text={generateShortcutText(
            localize("replaceWithNewButton"),
            isModifierActive
          )}
          icon={this.renderIcon(faPlusSquare)}
          onClick={this.handleReplaceWithNewClick}
        />
      ];
      if (!swapping && !includeRunningOrderInfo) {
        buttons.push(
          <ToolbarItem
            key="modifyCurrentButton"
            text={generateShortcutText(
              localize("modifyButton"),
              isModifierActive
            )}
            icon={this.renderIcon(faEdit)}
            onClick={this.handleUpdateMosObjectClick}
          />
        );
      }
      return buttons;
    }
    return (
      <ToolbarItem
        key="insertButton"
        text={generateShortcutText(localize("insertButton"), isModifierActive)}
        icon={this.renderIcon(faSignInAlt)}
        onClick={this.handleInsertClick}
      />
    );
  };

  render() {
    const {
      isModifierActive,
      displayRenderMedia,
      mediaEngineEnabled,
      publishingEnabled,
      editing,
      assetType,
      swapping,
      showSaveItemDialog,
      footerHeight,
      showSavedItems,
      placeholderId
    } = this.props;
    return (
      <Toolbar bottom={footerHeight} top="1.5rem" maxWidth="12rem">
        <ToolbarItem
          key="preview"
          text={localize("previewButton")}
          icon={this.renderIcon(faCamera)}
          onClick={this.handlePreviewClick}
        />
        {!placeholderId && showSaveItemDialog && showSavedItems && (
          <ToolbarItem
            key="saveItem"
            text={localize("saveItemButton")}
            icon={this.renderIcon(faSave)}
            onClick={showSaveItemDialog}
          />
        )}
        {!placeholderId &&
        (editing ||
          assetType === AssetTypes.GRAPHIC_TEMPLATE ||
          assetType === AssetTypes.GRAPHIC_MOS_OBJECT) &&
        !swapping ? (
          <ToolbarItem
            key="swapTemplates"
            text={localize("swapTemplatesButton")}
            icon={this.renderIcon(faExchangeAlt)}
            onClick={this.props.showChangeTemplateDialog}
          />
        ) : (
          ""
        )}
        {!placeholderId && mediaEngineEnabled && publishingEnabled ? (
          <ToolbarItem
            text={generateShortcutText(
              localize("publishButton"),
              isModifierActive
            )}
            icon={this.renderIcon(faCloudUploadAlt)}
            onClick={this.props.showPublishingDialog}
          />
        ) : (
          ""
        )}
        {!placeholderId && displayRenderMedia ? (
          <ToolbarItem
            key="renderMedia"
            text={generateShortcutText(
              localize("renderMediaButton"),
              isModifierActive
            )}
            icon={this.renderIcon(faFileVideo)}
            onClick={this.props.showRenderMediaDialog}
          />
        ) : (
          ""
        )}
        {this.renderMosControls()}
      </Toolbar>
    );
  }
}

EditorSideBarContent.contextTypes = {
  router: PropTypes.object
};

EditorSideBarContent.defaultProps = {
  placeholderId: "",
  previewImg: "",
  thumbnailUrl: "",
  title: null,
  mediaEngineServer: null,
  showSaveItemDialog: null,
  publishingEnabled: false,
  mediaEngineEnabled: false,
  onComplete: null,
  sendFromCamio: false,
  includeRunningOrderInfo: false
};

EditorSideBarContent.propTypes = {
  createNewMosObject: PropTypes.func.isRequired,
  createMosObjectFromExisting: PropTypes.func.isRequired,
  replaceMosObjectWithNew: PropTypes.func.isRequired,
  updateMosObject: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  previewImg: PropTypes.string,
  isMosObject: PropTypes.bool.isRequired,
  placeholderId: PropTypes.string,
  title: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  forceEditNew: PropTypes.bool.isRequired,
  shortcuts: PropTypes.shape({
    insert: PropTypes.arrayOf(PropTypes.string),
    publish: PropTypes.arrayOf(PropTypes.string),
    modify: PropTypes.arrayOf(PropTypes.string),
    saveImage: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  isModifierActive: PropTypes.bool.isRequired,
  showPublishingDialog: PropTypes.func.isRequired,
  showChangeTemplateDialog: PropTypes.func.isRequired,
  displayRenderMedia: PropTypes.bool.isRequired,
  showRenderMediaDialog: PropTypes.func.isRequired,
  publishingEnabled: PropTypes.bool,
  mediaEngineEnabled: PropTypes.bool,
  assetType: PropTypes.string.isRequired,
  swapping: PropTypes.bool.isRequired,
  showSaveItemDialog: PropTypes.func,
  footerHeight: PropTypes.string.isRequired,
  generatePreview: PropTypes.func.isRequired,
  generatingPreview: PropTypes.bool.isRequired,
  showSavedItems: PropTypes.bool.isRequired,
  onComplete: PropTypes.func,
  setAppMode: PropTypes.func.isRequired,
  sendFromCamio: PropTypes.bool,
  includeRunningOrderInfo: PropTypes.bool,
  connectionsLoaded: PropTypes.bool.isRequired,
  checkConnectionStatus: PropTypes.func.isRequired
};

const makeMapStateToProps = () => {
  const getConfigValue = makeGetConfigValue();
  const mapStateToProps = state => ({
    previewImg: state.asset.asset.previewImg,
    assetType: state.assets.assetType,
    thumbnailUrl: state.asset.asset.thumbnailUrl,
    editing: state.asset.editing,
    isMosObject: state.asset.isMosObject,
    footerHeight: state.app.footerHeight,
    placeholderId: state.app.placeholderId,
    showSavedItems:
      getConfigValue(state, "userConfig", "savedItemsDisplay", "show") ===
      "show",
    title:
      state.asset.asset.title ||
      (state.asset.asset.mosObjectId
        ? state.asset.asset.mosObjectId.replace("\\", "_")
        : ""),
    displayRenderMedia:
      getConfigValue(state, "userConfig", "renderMediaDisplay", "hide") ===
      "show",
    forceEditNew: getConfigValue(state, "mos", "defaultEditNew", false),
    shortcuts: state.keyShortcuts.shortcuts,
    isModifierActive: state.keyShortcuts.isModifierActive,
    mediaEngineEnabled:
      state.app.connections.mediaEngine.connected &&
      state.app.connections.mediaEngine.licensed,
    publishingEnabled:
      state.app.connections.publishing.connected &&
      state.app.connections.publishing.licensed &&
      state.app.config.userConfig.publishDisplay === "show",
    swapping: state.asset.swapping,
    generatingPreview: state.asset.generatingPreview,
    connectionsLoaded: state.app.connections.mediaEngine.connected !== undefined
  });
  return mapStateToProps;
};

const mapActionCreators = {
  createNewMosObject: assetActions.createNewMosObject,
  createMosObjectFromExisting: assetActions.createMosObjectFromExisting,
  replaceMosObjectWithNew: assetActions.replaceMosObjectWithNew,
  updateMosObject: assetActions.updateMosObject,
  generatePreview: assetActions.generatePreview,
  setAppMode: appActions.setAppMode,
  checkConnectionStatus: appActions.checkConnectionStatus
};

export default connect(
  makeMapStateToProps,
  mapActionCreators
)(EditorSideBarContent);



// WEBPACK FOOTER //
// ./app/components/Editor/EditorSideBarContent.js